import React, { useEffect, useState, useCallback, useMemo } from "react"
import { Row, Col, Spinner, Alert, Card, Badge, ButtonGroup, Button } from "react-bootstrap"
import FsLightbox from "fslightbox-react"
import moment from "moment"
import API from "@/common/API"
import Settings from "@/common/Settings"
import { useTranslation } from "react-i18next"
import PhotoCommentModal from "@/components/Places/PhotoCommentModal"
import "./modernGallery.css"

/**
 * Helper to convert thumbnail URLs to original/optimized URLs
 * Sanitizes URLs to prevent javascript: or malformed URI injection
 */
export const getOptimizedUrl = item => {
  if (!item) return ""
  const raw = item.original || item.image || item.thumbnail
  if (!raw || typeof raw !== "string") return ""
  const trimmed = raw.trim()
  if (/^(javascript|vbscript):/i.test(trimmed)) return ""
  if (/^data:(?!image\/)/i.test(trimmed)) return ""
  return trimmed.replace("/files/thumb/", "/files/")
}

/**
 * ModernVisitGallery - Modern photo gallery component with lightbox
 * Displays photos with date, author, source badge and lightbox view
 */
const ModernVisitGallery = ({ id }) => {
  const { t } = useTranslation("global")
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [photoFilter, setPhotoFilter] = useState("all")
  const [selectedCommentPhoto, setSelectedCommentPhoto] = useState(null)
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1,
  })

  const bindData = useCallback(async () => {
    if (!id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      let query = "id=" + encodeURIComponent(id)
      let requestAPI = await API.getAction("place/ListImages", query)

      if (requestAPI?.data?.status === "ok") {
        let rawResponse = Array.isArray(requestAPI.data.response)
          ? requestAPI.data.response
          : []
        let response = rawResponse
          .filter(Boolean)
          .map(item => {
            if (typeof item !== "object") return null
            return {
              ...item,
              loading: "lazy",
              thumbnailHeight: "100",
              thumbnailWidth: "100",
            }
          })
          .filter(Boolean)

        setImages(response)
      } else {
        throw new Error(requestAPI?.data?.message || t("Failed to load images"))
      }
    } catch (err) {
      console.error("Error fetching images:", err)
      setError(err?.message || t("Error loading photos"))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    bindData()
  }, [bindData])

  const filteredImages = useMemo(() => {
    if (photoFilter === "all") return images
    return images.filter(img => img.source === photoFilter)
  }, [images, photoFilter])

  const openLightbox = useCallback(
    index => {
      setLightboxController({
        toggler: !lightboxController.toggler,
        slide: index + 1,
      })
    },
    [lightboxController.toggler],
  )

  const lightboxSources = useMemo(() => {
    return filteredImages
      .map(img => getOptimizedUrl(img))
      .filter(Boolean)
  }, [filteredImages])

  const countCreation = useMemo(() => {
    return (images || []).filter(img => img.source === "creation").length
  }, [images])

  const countVisit = useMemo(() => {
    return (images || []).filter(img => img.source === "visit").length
  }, [images])

  const handleRetry = () => {
    bindData()
  }

  if (loading) {
    return (
      <Card className="my-4 shadow-sm rounded-3">
        <Card.Body className="p-0">
          <div className="modern-gallery-loading text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">{t("Loading photos...")}</p>
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="my-4 shadow-sm rounded-3">
        <Card.Body className="p-3">
          <Alert variant="warning" className="text-center mb-0">
            <i
              className="fa fa-exclamation-triangle mb-2"
              style={{ fontSize: "2rem" }}
            ></i>
            <p className="mb-2">{error}</p>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleRetry}
            >
              <i className="fa fa-refresh me-2"></i>
              {t("Try again")}
            </button>
          </Alert>
        </Card.Body>
      </Card>
    )
  }

  if (!images || images.length === 0) {
    return null
  }

  return (
    <Card className="my-4 shadow-sm rounded-3 overflow-hidden">
      <Card.Body className="p-0">
        <div className="modern-gallery">
          {/* Gallery Header */}
          <div className="modern-gallery-header mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0 d-flex align-items-center fw-bold">
              <i className="fa fa-images me-2 text-primary"></i>
              {t("Photo Gallery")}
            </h6>
            <Badge bg="secondary" pill className="photo-count">
              {filteredImages.length} {filteredImages.length === 1 ? t("photo") : t("photos")}
            </Badge>
          </div>

          {/* Source Filter if both types exist */}
          {countCreation > 0 && countVisit > 0 && (
            <ButtonGroup size="sm" className="shadow-2xs">
              <Button
                variant={photoFilter === "all" ? "primary" : "outline-secondary"}
                className="py-0 px-2"
                style={{ fontSize: "0.75rem" }}
                onClick={() => setPhotoFilter("all")}
              >
                Todas ({images.length})
              </Button>
              <Button
                variant={photoFilter === "creation" ? "primary" : "outline-secondary"}
                className="py-0 px-2"
                style={{ fontSize: "0.75rem" }}
                onClick={() => setPhotoFilter("creation")}
              >
                Iniciales ({countCreation})
              </Button>
              <Button
                variant={photoFilter === "visit" ? "primary" : "outline-secondary"}
                className="py-0 px-2"
                style={{ fontSize: "0.75rem" }}
                onClick={() => setPhotoFilter("visit")}
              >
                Visitas ({countVisit})
              </Button>
            </ButtonGroup>
          )}
        </div>
      </div>

      {/* Photos Grid */}
      <Row className="modern-gallery-grid g-2">
        {filteredImages.map((image, index) => {
          const isCreation = image.source === "creation"
          const dateFormatted = image.date ? moment(image.date).format("DD/MM/YYYY") : null
          const timeAgo = image.date ? moment(image.date).fromNow() : null

          return (
            <Col key={image.id || index} xs={6} sm={4} md={3} lg={3}>
              <Card
                className="modern-gallery-item h-100 shadow-2xs border overflow-hidden"
                onClick={() => openLightbox(index)}
                style={{ cursor: "pointer", transition: "transform 0.15s ease, box-shadow 0.15s ease" }}
              >
                <div className="modern-gallery-image-container position-relative" style={{ height: "130px", background: "#f8f9fa" }}>
                  <img
                    src={getOptimizedUrl(image)}
                    alt={image.originalAlt || image.note || `Photo ${index + 1}`}
                    className="modern-gallery-image w-100 h-100"
                    style={{ objectFit: "cover" }}
                    loading="lazy"
                    onError={e => {
                      const target = e.currentTarget
                      if (!target.dataset.retried && image.thumbnail) {
                        target.dataset.retried = "true"
                        target.src = image.thumbnail
                      } else {
                        target.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIGltYWdlbjwvdGV4dD48L3N2Zz4="
                      }
                    }}
                  />

                  {/* Top Badges */}
                  <div className="position-absolute top-0 start-0 m-1 d-flex flex-wrap gap-1">
                    {isCreation ? (
                      <Badge bg="primary" className="fw-semibold shadow-xs" style={{ fontSize: "0.62rem" }}>
                        Inicial
                      </Badge>
                    ) : (
                      <Badge bg="success" className="fw-semibold shadow-xs" style={{ fontSize: "0.62rem" }}>
                        Visita
                      </Badge>
                    )}
                  </div>

                  {dateFormatted && (
                    <div className="position-absolute bottom-0 start-0 m-1">
                      <span
                        className="badge bg-dark bg-opacity-75 text-white fw-normal shadow-xs"
                        style={{ fontSize: "0.62rem" }}
                      >
                        <i className="far fa-calendar-alt me-1" />
                        {dateFormatted}
                      </span>
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="modern-gallery-overlay">
                    <div className="modern-gallery-overlay-content">
                      <i className="fa fa-search-plus"></i>
                    </div>
                  </div>
                </div>

                {/* Photo Info Footer */}
                <div className="p-2 bg-white small border-top text-start" style={{ fontSize: "0.74rem" }}>
                  <div className="d-flex align-items-center justify-content-between text-muted mb-1">
                    <span className="fw-semibold text-dark text-truncate" style={{ maxWidth: "60%" }}>
                      {image.agentName ? (
                        <>
                          <i className="far fa-user me-1 text-primary" />
                          {image.agentName}
                        </>
                      ) : (
                        isCreation ? "Registro inicial" : "Visita"
                      )}
                    </span>
                    {timeAgo && (
                      <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                        {timeAgo}
                      </span>
                    )}
                  </div>

                  {image.note && (
                    <div className="text-muted text-truncate" title={image.note} style={{ fontSize: "0.7rem" }}>
                      <i className="far fa-comment-dots me-1 text-secondary" />
                      {image.note}
                    </div>
                  )}

                  {/* Comment & Mention Action Bar */}
                  <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 rounded-pill px-2 py-0.5"
                      style={{ fontSize: "0.68rem" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedCommentPhoto(image)
                      }}
                      title="Comentar o mencionar a un usuario"
                    >
                      <i className="far fa-comment-dots" />
                      <span>Comentar</span>
                    </button>
                    <span className="text-muted" style={{ fontSize: "0.65rem" }}>
                      <i className="fas fa-at me-0.5 text-primary opacity-75" /> mención
                    </span>
                  </div>
                </div>
              </Card>
            </Col>
          )
        })}
      </Row>

        </div>
      </Card.Body>
      {/* Lightbox */}
      {lightboxSources.length > 0 && (
        <FsLightbox
          toggler={lightboxController.toggler}
          sources={lightboxSources}
          slide={lightboxController.slide}
          type="image"
          maxYoutubeVideoDimensions={{ width: 900, height: 600 }}
        />
      )}

      {/* Photo Comment and Mentions Modal */}
      {selectedCommentPhoto && (
        <PhotoCommentModal
          show={!!selectedCommentPhoto}
          onHide={() => setSelectedCommentPhoto(null)}
          photo={selectedCommentPhoto}
          placeId={id}
        />
      )}
    </Card>
  )
}

export default ModernVisitGallery
